import {isNil} from 'lodash';
import {useEffect, useState, useContext} from 'react';
import useFileUpload from '../../../../../../shared/hooks/use-file-upload.hook';
import {Check, CheckType} from '../../../../../../shared/model/check.model';
import {FieldType} from '../../../../../../shared/model/field.model';
import {NxFile} from '../../../../../../shared/model/nx-file.model';
import {Phase} from '../../../../../../shared/model/phase.model';
import {NumberHelper} from '../../../../../../utils/number-helper';
import {AppSnackbarContext} from '../../../../../shared/app-snackbar-provider/AppSnackbarProvider';
import {FALSE_STRING} from '../../../../../shared/form-bolean-option-values/YesNoSelectOptions';
import {CheckValue, CheckValueTO} from './check-value.model';
import {handleNxFiles, parseSavedCheck} from './check-values-utils';
import {useFileLoadingTracker} from './use-file-loading-tracker.hook';

const getInitValues = (checks: Check[], valuesTO?: CheckValueTO[]): CheckValue[] =>
  checks.map(check => {

    const {automatic, id, proofType, type, required} = check;
    const savedValue = valuesTO?.find(({checkId}) => checkId === id)?.value;

    let value = isNil(savedValue) && type !== CheckType.MANUAL
      ? FALSE_STRING
      : parseSavedCheck(savedValue, proofType);
    value = handleNumericProofType(value, proofType);

    return {checkId: id, isAutomatic: automatic, required, value, fieldType: proofType};
  });

function handleNumericProofType<T>(value: T, proofType: FieldType | undefined): T | number | undefined{
  switch (proofType) {
    case FieldType.CASH:
    case FieldType.INTEGER:
    case FieldType.DOUBLE:
    case FieldType.PERCENTAGE:
      return NumberHelper.toNumberOrUndefined(value);
    default:
      return value;
  }
}

export default function useCustomCheckInitValues(config?: Phase,
                                                 valuesTO?: CheckValueTO[]): { 
  checkValues: (CheckValue | CheckValue<number> | CheckValue<NxFile>)[] | undefined;
  areAllFilesLoaded: boolean;
} {

  const {downloadFile} = useFileUpload();
  const [checkInitValues, setCheckInitValues] = useState<(CheckValue | CheckValue<NxFile>)[]>();
  const {showErrorMessage} = useContext(AppSnackbarContext);
  const {trackFileDownload, areAllFilesLoaded, setFileLoadingStatus} = useFileLoadingTracker();

  useEffect(() => {
    if (isNil(config)) {
      return;
    }

    // Flag to prevent state updates if the component unmounts or the effect re-runs before promises resolve
    let isCancelled = false;

    const checks = config.checkGroups.flatMap(checkGroup => checkGroup.checks);
    
    // Initialize file loading status for all file fields
    const fileFields = checks.filter(check => 
      check.proofType === FieldType.FILE || check.proofType === FieldType.DOCUMENT
    );
    
    if (fileFields.length > 0) {
      const initialStatus = fileFields.reduce((acc, field) => {
        acc[String(field.id)] = false;
        return acc;
      }, {} as {[key: string]: boolean});
      setFileLoadingStatus(initialStatus);
    }
    
    const promises = getInitValues(checks, valuesTO)
      .map(value => handleNxFiles(
        value, 
        downloadFile, 
        (fileBasicData, checkId, downloadFileFn) => trackFileDownload(fileBasicData, checkId, downloadFileFn)
      ));

    Promise.all(promises)
      .then(resolvedData => {
        if (!isCancelled) {
          setCheckInitValues(resolvedData as (CheckValue | CheckValue<NxFile>)[]);
        }
      })
      .catch(e => {
        if (!isCancelled) {
          showErrorMessage(`Error when handling check files: ${e.message}`)
        }
    });

    return () => {
      isCancelled = true;
    };
  }, [config, valuesTO]);

  return { 
    checkValues: checkInitValues,
    areAllFilesLoaded 
  };
}
